import { defineExperiment } from '@learnforge/demo-render/harness';
import type {
  CustomExperimentSpec,
  PanelRow,
  ActionBarSpec,
} from '@learnforge/demo-render/harness';
import { createReadout, type ReadoutHandle, type ReadoutRow } from '@learnforge/demo-render';
import { createSvgEl } from '@learnforge/demo-render';
import { niceCeil } from '@learnforge/demo-math/numeric';
import type { FinanceContext } from '../types';
import type { FinanceKey } from '../locales/keys';
import { createChart, type ChartHandle } from '../render/chart';
import { createLineSeries, type LineSeriesHandle } from '../render/line-series';
import { createAreaStack, type AreaStackHandle, type Band } from '../render/area-stack';
import { createCoinIcon } from '../render/coin';
import { futureValueSeries, solveContributionForGoal } from '../finance/annuity';
import { type Point } from '../finance/series';
import { formatMoney, formatPercent, formatYears } from '../finance/format';

// ── State ─────────────────────────────────────────────────────────────────────

export interface SavingsGoalState {
  monthly: number;
  rate: number;
  years: number;
  principal: number; // optional lump sum
  target: number; // goal (advanced: goal-seek)
  goalSeek: boolean; // advanced: solve monthly for target instead of plotting monthly
}

// ── Pure model (unit-tested) ──────────────────────────────────────────────────

export interface SavingsGoalModel {
  bands: Band[];
  goalLine?: Point[] | undefined;
  readout: ReadoutRow[];
}

/**
 * Build the model for the savings-goal experiment.
 * Returns two stacked bands (contributions, growth) that sum to the
 * futureValueSeries balance at each integer year.
 */
export function savingsGoalModel(state: SavingsGoalState): SavingsGoalModel {
  const { monthly, rate, years, principal, target, goalSeek } = state;

  const fv = futureValueSeries(monthly, rate, 12, years, { principal });

  // Build contributions band: running money paid in (monthly*12*year + principal),
  // monotone non-decreasing, capped to total balance.
  const contributionPoints: Point[] = fv.points.map((p, i) => {
    const year = p.t;
    // Running contributions = principal + (monthly * 12 * year)
    const runningContrib = principal + monthly * 12 * year;
    // Cap at the actual balance (growth can't be negative)
    const cappedContrib = Math.min(runningContrib, p.value);
    return { t: p.t, value: i === 0 ? principal : cappedContrib };
  });

  // Growth band: balance - contributions (always >= 0)
  const growthPoints: Point[] = fv.points.map((p, i) => {
    const contrib = contributionPoints[i]!.value;
    return { t: p.t, value: Math.max(0, p.value - contrib) };
  });

  const bands: Band[] = [
    { id: 'contributions', points: contributionPoints, className: 'fin-area-contrib' },
    { id: 'growth', points: growthPoints, className: 'fin-area-growth' },
  ];

  // Readout
  const totalContributed = principal + monthly * 12 * years;
  const finalBalance = fv.points.at(-1)?.value ?? 0;
  const totalGrowth = Math.max(0, finalBalance - totalContributed);

  const readout: ReadoutRow[] = [
    {
      id: 'total-contributed',
      lhs: 'stat.total-contributed.label',
      relation: '=',
      rhs: formatMoney(totalContributed),
    },
    {
      id: 'total-growth',
      lhs: 'stat.total-growth.label',
      relation: '=',
      rhs: formatMoney(totalGrowth),
    },
    {
      id: 'final-balance',
      lhs: 'stat.final-balance.label',
      relation: '=',
      rhs: formatMoney(finalBalance),
      emphasis: 'invariant',
    },
  ];

  let goalLine: Point[] | undefined;

  if (goalSeek) {
    const req = requiredMonthly(state);
    readout.push({
      id: 'required-monthly',
      lhs: 'stat.required-monthly.label',
      relation: '=',
      rhs: formatMoney(req),
      emphasis: 'invariant',
    });

    // Horizontal goal line spanning t=0..years at the target value
    goalLine = [
      { t: 0, value: target },
      { t: years, value: target },
    ];
  }

  const result: SavingsGoalModel = { bands, readout };
  if (goalLine !== undefined) {
    result.goalLine = goalLine;
  }
  return result;
}

/**
 * Solve for the required monthly contribution to reach `target` in `years`.
 * Subtracts any principal's future value first, then applies solveContributionForGoal.
 */
export function requiredMonthly(state: SavingsGoalState): number {
  const { target, rate, years, principal } = state;

  // Future value of the principal alone (no contributions)
  let principalFV = principal;
  if (rate > 0 && principal > 0) {
    // Compound monthly for `years` years
    const i = rate / 12;
    const m = 12 * years;
    principalFV = principal * Math.pow(1 + i, m);
  }

  // Amount the contributions need to reach (after principal grows)
  const remainingTarget = Math.max(0, target - principalFV);

  if (remainingTarget === 0) {
    return 0;
  }

  return solveContributionForGoal(remainingTarget, rate, 12, years);
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const CHART_OPTS = {
  width: 800,
  height: 460,
  padding: { top: 24, right: 32, bottom: 44, left: 80 },
  yTickCount: 5,
  xTickCount: 6,
} as const;

function defaultState(advanced: boolean): SavingsGoalState {
  return {
    monthly: 200,
    rate: 0.06,
    years: 10,
    principal: 0,
    target: 30000,
    goalSeek: advanced,
  };
}

const PRESETS = [
  { id: 'emergency-fund', labelKey: 'preset.emergency-fund.label' as FinanceKey },
  { id: 'house-deposit', labelKey: 'preset.house-deposit.label' as FinanceKey },
  { id: 'gap-year', labelKey: 'preset.gap-year.label' as FinanceKey },
];

function applyPresetLocal(state: SavingsGoalState, id: string): SavingsGoalState {
  switch (id) {
    case 'emergency-fund':
      // 3-month emergency fund: target £5,000 in 2 years
      return {
        ...state,
        monthly: 200,
        rate: 0.04,
        years: 2,
        principal: 500,
        target: 5000,
        goalSeek: state.goalSeek,
      };
    case 'house-deposit':
      // Save a house deposit: target £30,000 in 5 years
      return {
        ...state,
        monthly: 400,
        rate: 0.05,
        years: 5,
        principal: 5000,
        target: 30000,
        goalSeek: state.goalSeek,
      };
    case 'gap-year':
      // Travel fund: target £10,000 in 3 years
      return {
        ...state,
        monthly: 250,
        rate: 0.04,
        years: 3,
        principal: 1000,
        target: 10000,
        goalSeek: state.goalSeek,
      };
    default:
      return state;
  }
}

function randomise(state: SavingsGoalState, prng: (max?: number) => number): SavingsGoalState {
  const monthlyOptions = [100, 150, 200, 300, 400, 500];
  const rateOptions = [0.03, 0.04, 0.05, 0.06, 0.07, 0.08];
  const yearOptions = [3, 5, 7, 10, 15, 20];
  const principalOptions = [0, 500, 1000, 2000, 5000];
  const targetOptions = [5000, 10000, 20000, 30000, 50000];

  return {
    ...state,
    monthly: monthlyOptions[Math.floor(prng() * monthlyOptions.length)] ?? 200,
    rate: rateOptions[Math.floor(prng() * rateOptions.length)] ?? 0.06,
    years: yearOptions[Math.floor(prng() * yearOptions.length)] ?? 10,
    principal: principalOptions[Math.floor(prng() * principalOptions.length)] ?? 0,
    target: targetOptions[Math.floor(prng() * targetOptions.length)] ?? 20000,
  };
}

// ── Spec factory ──────────────────────────────────────────────────────────────

function buildSpec(): CustomExperimentSpec<SavingsGoalState, FinanceKey, FinanceContext> {
  return {
    labelKey: 'experiment.savings-goal.label',
    demoId: 'finance',
    advancedKey: 'savings-goal',

    initialState: (ctx) => defaultState(ctx.advanced),

    applyPreset: (state, id) => applyPresetLocal(state, id),

    panel(state, ctx): PanelRow<FinanceKey, SavingsGoalState>[] {
      const rows: PanelRow<FinanceKey, SavingsGoalState>[] = [
        {
          kind: 'slider',
          id: 'monthly',
          labelKey: 'param.monthly.label',
          min: 50,
          max: 2000,
          step: 50,
          value: state.monthly,
          format: formatMoney,
          onChange: (v) => ({ ...state, monthly: v }),
        },
        {
          kind: 'slider',
          id: 'rate',
          labelKey: 'param.rate.label',
          min: 0.01,
          max: 0.15,
          step: 0.005,
          value: state.rate,
          format: (v) => formatPercent(v, 1),
          onChange: (v) => ({ ...state, rate: v }),
        },
        {
          kind: 'slider',
          id: 'years',
          labelKey: 'param.years.label',
          min: 1,
          max: 30,
          step: 1,
          value: state.years,
          format: formatYears,
          onChange: (v) => ({ ...state, years: v }),
        },
        {
          kind: 'slider',
          id: 'principal',
          labelKey: 'param.principal.label',
          min: 0,
          max: 20000,
          step: 500,
          value: state.principal,
          format: formatMoney,
          onChange: (v) => ({ ...state, principal: v }),
        },
        {
          kind: 'advanced-toggle',
          labelKey: 'panel.advanced',
        },
      ];

      if (ctx.advanced) {
        rows.splice(4, 0, {
          kind: 'toggle',
          labelKey: 'param.goalSeek.label',
          value: state.goalSeek,
          onChange: (v) => ({ ...state, goalSeek: v }),
        });
        rows.splice(5, 0, {
          kind: 'slider',
          id: 'target',
          labelKey: 'param.target.label',
          min: 1000,
          max: 200000,
          step: 1000,
          value: state.target,
          format: formatMoney,
          onChange: (v) => ({ ...state, target: v }),
        });
      }

      return rows;
    },

    actions(_state, ctx): ActionBarSpec<FinanceKey, SavingsGoalState> {
      return {
        onReset: () => defaultState(ctx.advanced),
        onRandomise: (s) => randomise(s, ctx.prng),
        presets: PRESETS,
        onPreset: (s, id) => applyPresetLocal(s, id),
      };
    },

    applyShotState(state): SavingsGoalState {
      // A goal-seek pose where contributions+growth meets the goal line
      return {
        ...state,
        monthly: 200,
        rate: 0.06,
        years: 10,
        principal: 0,
        target: 16387,
        goalSeek: true,
      };
    },

    createRender(ctx, host, controls) {
      // Build chart
      const chart: ChartHandle = createChart(host, {
        ...CHART_OPTS,
        formatX: (v) => formatYears(v),
        formatY: (v) => formatMoney(v),
      });

      // Coin icon at the y-axis head
      const coinGroup = createCoinIcon(18);
      coinGroup.setAttribute(
        'transform',
        `translate(${CHART_OPTS.padding.left - 4}, ${CHART_OPTS.padding.top})`,
      );
      chart.svg.appendChild(coinGroup);

      // Stacked area layer
      const areaStack: AreaStackHandle = createAreaStack(chart);

      // Line series for the goal line
      const lines: LineSeriesHandle = createLineSeries(chart);

      // Legend group
      const legendGroup = createSvgEl('g', { class: 'fin-legend' });
      chart.svg.appendChild(legendGroup);

      // Readout — lives OUTSIDE ctx.root per the harness contract
      const readout: ReadoutHandle = createReadout(document.getElementById('readout') ?? host);

      // Store yDomain for pointer inversion (updated in update())
      let currentYDomain: [number, number] = [0, 1];
      let currentGoalSeek = false;

      function updateLegend(showGoalLine: boolean): void {
        while (legendGroup.firstChild) {
          legendGroup.removeChild(legendGroup.firstChild);
        }
        const { width, padding } = CHART_OPTS;
        // Right-anchor labels so they sit in empty upper-right space and
        // never overlap the rising stacked-area bands.
        const labelRightX = width - padding.right - 8;
        const swatchLen = 16;
        const swatchGap = 6;
        const legendY = padding.top + 16;

        const areaItems: Array<{ cls: string; key: FinanceKey }> = [
          { cls: 'fin-area-contrib', key: 'series.contributions.label' },
          { cls: 'fin-area-growth', key: 'series.growth.label' },
        ];
        if (showGoalLine) {
          areaItems.push({ cls: 'fin-line-goal', key: 'series.goal.label' });
        }

        areaItems.forEach((item, i) => {
          const rowY = legendY + i * 18;
          const swatchX2 = labelRightX - swatchGap;
          const swatchX1 = swatchX2 - swatchLen;

          if (item.cls === 'fin-line-goal') {
            const lineEl = createSvgEl('line', {
              class: `${item.cls} fin-legend-dot`,
              x1: swatchX1,
              y1: rowY,
              x2: swatchX2,
              y2: rowY,
            });
            legendGroup.appendChild(lineEl);
          } else {
            const rect = createSvgEl('rect', {
              class: item.cls,
              x: swatchX1,
              y: rowY - 5,
              width: swatchLen,
              height: 10,
              rx: 2,
            });
            legendGroup.appendChild(rect);
          }

          const label = createSvgEl('text', {
            class: 'fin-legend',
            x: labelRightX,
            y: rowY,
            'text-anchor': 'end',
            'dominant-baseline': 'middle',
          });
          label.textContent = ctx.i18n.t(item.key);
          legendGroup.appendChild(label);
        });
      }

      // Visible drag handle: a small circle marker at the right edge of the goal line.
      // Signals the goal line is draggable; updated in update() when goal-seek is on.
      const dragHandleGroup = createSvgEl('g', { class: 'fin-drag-handle-group' });
      const dragHandleOuter = createSvgEl('circle', {
        class: 'fin-drag-handle',
        r: 8,
        cx: 0,
        cy: 0,
      });
      const dragHandleInner = createSvgEl('circle', {
        class: 'fin-drag-handle-inner',
        r: 3,
        cx: 0,
        cy: 0,
      });
      dragHandleGroup.appendChild(dragHandleOuter);
      dragHandleGroup.appendChild(dragHandleInner);
      dragHandleGroup.setAttribute('display', 'none');
      chart.svg.appendChild(dragHandleGroup);

      // Draggable goal line: pointer handler on chart.svg
      function onPointerMove(evt: PointerEvent): void {
        // Only drag when goal-seek is active and button is pressed
        if (evt.buttons === 0) return;
        if (!currentGoalSeek) return;
        onPointerDrag(evt);
      }

      function onPointerDrag(evt: PointerEvent): void {
        // Guard: getScreenCTM may return null in non-DOM/test contexts
        const ctm = chart.svg.getScreenCTM();
        if (!ctm) return;

        const svgPoint = chart.svg.createSVGPoint();
        svgPoint.x = evt.clientX;
        svgPoint.y = evt.clientY;
        const localPt = svgPoint.matrixTransform(ctm.inverse());

        // Invert y-scale to data value
        const { height, padding } = CHART_OPTS;
        const plotTop = padding.top;
        const plotBottom = height - padding.bottom;
        const [yMin, yMax] = currentYDomain;

        const newTarget = yMax - ((localPt.y - plotTop) / (plotBottom - plotTop)) * (yMax - yMin);

        // Clamp to [0, yMax]
        const clamped = Math.max(0, Math.min(yMax, newTarget));

        controls.setState((s) => ({ ...s, target: Math.round(clamped) }));
      }

      chart.svg.addEventListener('pointermove', onPointerMove);

      // Register cleanup so dispose removes the listener
      controls.registerCleanup(() => {
        chart.svg.removeEventListener('pointermove', onPointerMove);
      });

      return {
        update(state: SavingsGoalState): void {
          currentGoalSeek = state.goalSeek;
          const m = savingsGoalModel(state);
          const finalBalance = m.bands.reduce((sum, b) => {
            const last = b.points.at(-1)?.value ?? 0;
            return sum + last;
          }, 0);

          const yMax = niceCeil(Math.max(finalBalance, state.goalSeek ? state.target : 0) * 1.1);

          currentYDomain = [0, yMax];

          chart.setScales({
            xDomain: [0, state.years],
            yDomain: currentYDomain,
          });

          // Update stacked area
          areaStack.setBands(m.bands);

          // Update goal line (only in goal-seek mode)
          if (m.goalLine) {
            lines.setSeries('goal', m.goalLine, 'fin-line-goal');

            // Position drag handle at the mid-x of the goal line, at the target y
            const midT = state.years / 2;
            const handlePt = chart.toScreen({ t: midT, value: state.target });
            dragHandleOuter.setAttribute('cx', String(handlePt.x));
            dragHandleOuter.setAttribute('cy', String(handlePt.y));
            dragHandleInner.setAttribute('cx', String(handlePt.x));
            dragHandleInner.setAttribute('cy', String(handlePt.y));
            dragHandleGroup.setAttribute('display', '');
          } else {
            lines.clear('goal');
            dragHandleGroup.setAttribute('display', 'none');
          }

          updateLegend(state.goalSeek);
          readout.update(m.readout.map((r) => ({ ...r, lhs: ctx.i18n.t(r.lhs as FinanceKey) })));
        },

        dispose(): void {
          readout.destroy();
          areaStack.destroy();
          lines.destroy();
          chart.destroy();
        },
      };
    },
  };
}

export const experiment = defineExperiment(buildSpec());
